import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { PMDialog } from "../../../components/utils/PMDialog";
import { fetchDocumentFormTemplates, createDocumentForm } from "../../../api/documentForms";

function resolveFormId(response) {
  if (!response || response.errors) return null;
  return response.id ?? response.data?.id ?? null;
}

export default function NewDocumentFormDialog({
  open,
  onClose,
  subjectType,
  subjectId,
  defaultTemplateId,
  defaultTitle,
  onCreated,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedId("");
      setTitle("");
      return;
    }
    setLoading(true);
    setTitle(defaultTitle || "");
    setSelectedId(defaultTemplateId ? String(defaultTemplateId) : "");
    fetchDocumentFormTemplates({ active: true, per_page: 100 }).then((res) => {
      setTemplates(res?.data || []);
      setLoading(false);
    });
  }, [open, defaultTitle, defaultTemplateId]);

  useEffect(() => {
    if (!open || !defaultTemplateId) return;
    setSelectedId(String(defaultTemplateId));
  }, [defaultTemplateId, open]);

  useEffect(() => {
    const tpl = templates.find((item) => String(item.id) === String(selectedId));
    if (tpl && !title) setTitle(defaultTitle || tpl.title || "");
  }, [selectedId, templates, title, defaultTitle, open]);

  const handleCreate = async () => {
    const templateId = selectedId || (defaultTemplateId ? String(defaultTemplateId) : "");
    if (!templateId) {
      toast.error(t("documentForms.choose_template"));
      return;
    }
    setCreating(true);
    const res = await createDocumentForm({
      document_form_template_id: Number(templateId),
      title: title || templates.find((x) => String(x.id) === String(templateId))?.title,
      subject_type: subjectType || undefined,
      subject_id: subjectId || undefined,
    });
    setCreating(false);

    if (res?.errors) {
      toast.error(Array.isArray(res.errors) ? res.errors.join(", ") : t("documentForms.save_failed"));
      return;
    }

    const formId = resolveFormId(res);
    if (!formId) {
      toast.error(t("documentForms.save_failed"));
      return;
    }

    toast.success(
      res?.display_reference
        ? t("documentForms.created_with_reference", { reference: res.display_reference })
        : t("documentForms.created")
    );
    onClose?.();
    onCreated?.();
    navigate(`/dashboard/hse-ks/documents/form-archive/${formId}`);
  };

  return (
    <PMDialog open={open} onClose={onClose} title={t("documentForms.new")} size="md">
      <div className="space-y-4 px-1 py-2">
        <label className="block text-sm font-medium text-slate-700">
          {t("documentForms.template")}
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={loading || Boolean(defaultTemplateId)}
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50"
          >
            <option value="">{t("documentForms.choose_template")}</option>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.title}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm font-medium text-slate-700">
          {t("documentForms.form_title")}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            disabled={creating}
            onClick={handleCreate}
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {t("documentForms.create")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
